import type { AxiosError, AxiosInstance, ResponseType } from "axios";
import Axios from "axios";

/**
 * Base URL for the Axios instance, defaulting to localhost if environment variable is not set
 */
const clientBaseURL = import.meta.env.VITE_API ?? "http://localhost:8000";

/**
 * Create a reusable Axios instance with predefined configuration
 */
export const AXIOS_INSTANCE: AxiosInstance = Axios.create({
	baseURL: clientBaseURL,
	withCredentials: true, // Allows cookies and other credentials to be sent with requests
});

export interface RestAxiosFetcherProps<TData = unknown> {
	/** The endpoint URL */
	url: string;
	/** HTTP method (GET, POST, etc.) */
	method: string;
	/** Request payload (for POST, PUT, etc.) */
	params?: TData;
	/** Custom headers for the request */
	headers?: Record<string, string>;
	/** Expected response type (e.g., 'json', 'file') */
	responseType?: ResponseType;
	/** Abort signal to cancel the request if needed */
	signal?: AbortSignal;
	onProgress?: (progress: number) => void;
}

/**
 * REST fetcher function that uses the Axios instance to make requests
 *
 * @param props - The configuration properties for the request
 * @param axiosOptions
 * @param onProgress
 * @returns The response data from the request
 */
export const restAxiosFetcher = async <TResult, TData = unknown>(
	props: RestAxiosFetcherProps<TData>,
	axiosOptions?: Partial<RestAxiosFetcherProps<TData>>,
): Promise<TResult> => {
	const { url, method, params, headers, responseType, signal, onProgress } = {
		...props,
		...axiosOptions, // Merge axiosOptions into the default props
	};
	// Determine whether to send the payload as query parameters or in the body.
	const isBodyMethod = !["GET", "HEAD"].includes(method.toUpperCase());
	try {
		// Make an Axios request with the provided configuration
		const response = await AXIOS_INSTANCE.request<TResult>({
			url,
			method,
			//@ts-expect-error Probably not relevant...
			//TODO: Check this
			...(isBodyMethod ? { data: props.data } : { params }), // Use data for body methods.
			responseType,
      paramsSerializer: {
        dots: true,
      },
			signal, // Use the signal property to allow request cancellation
			headers: {
				withCredentials: true, // Ensure credentials are included in the headers
				...headers, // Merge any custom headers
			},
			onUploadProgress: onProgress
				? (event) => {
					if (event.total) {
						const percentCompleted = Math.round((event.loaded * 100) / event.total);
						onProgress(percentCompleted); // Report upload progress
					}
				}
				: undefined, // Include only if a callback is provided
			onDownloadProgress: onProgress
				? (event) => {
					if (event.total) {
						const percentCompleted = Math.round((event.loaded * 100) / event.total);
						onProgress(percentCompleted); // Report download progress
					}
				}
				: undefined, // Include only if a callback is provided
		});
		return response.data; // Return only the data from the response
	} catch (error) {
		console.error(error);
		if (Axios.isAxiosError(error)) {
			// Handle Axios-specific errors
			if (error.response) {
				// If the server responded with an error status code
				console.error(`Error: ${error.response.status} - ${error.response.data}`, error);
			}
			if (error.request) {
				// If the request was made but no response was received
				console.error("No response received from server", error);
			}
		} else {
			// Handle non-Axios errors
			console.error("An unexpected fetching error occurred", error as ErrorOptions | undefined);
		}
		return Promise.reject(error);
	}
};

export type ErrorType<Error> = AxiosError<Error>;
